import { useState } from "react";
import { Tooltip } from "antd";
import {
  WalletOutlined, ArrowUpOutlined, ArrowDownOutlined, DownloadOutlined, MailOutlined,
} from "@ant-design/icons";
import { toast } from "react-toastify";
import { API_BASE_URL } from "../../../../config";
import { fetchWallet } from "../../../services/customer360Service";
import { openInvoice } from "../../../services/invoiceService";
import SendInvoiceModal from "../../../invoices/SendInvoiceModal";
import {
  cardStyle, inr, dateTime, statusChip, StatTile, TileGrid, PanelHeader,
  PanelTable, PanelSkeleton, PanelError, usePanelData,
} from "./shared";

/**
 * Money in and out of the customer's wallet.
 *
 * Credits and debits share one ledger, so the sign carries the meaning: a green
 * +₹ is money they put in, a red −₹ is money the product took. Reading a column
 * of bare numbers and working out which is which is the thing this avoids.
 */
/* Only money coming in has a receipt. A debit is the product spending what was
   already paid for, and it is covered by the subscription invoice — offering a
   receipt there would promise a document that does not exist. */
const isRecharge = (row) =>
  Number(row.credit) > 0 || Number(row.added_balance) > 0;

/**
 * The pre-tax figure, if the row states one.
 *
 * Not computed from credit − GST. Whether the credit already includes the tax
 * or the tax sits on top of it is the server's answer, not something to be
 * inferred — and a wrong split here is a wrong number against a receipt. So the
 * base is printed only when a field actually carries it; otherwise the GST line
 * stands on its own, which is still true.
 */
const baseOf = (row) => {
  const v = row.base_amount ?? row.amount_before_gst ?? row.taxable_amount;
  return v === null || v === undefined || v === "" ? null : v;
};

const WalletTab = ({ userId, active, customerEmail = "" }) => {
  const p = usePanelData(fetchWallet, userId, active);
  const [sending, setSending] = useState(null);
  const [opening, setOpening] = useState(null);

  const download = async (row) => {
    setOpening(row.id);
    try {
      await openInvoice(`${API_BASE_URL}/invoices/wallet/${row.id}/download?user_id=${userId}`);
    } catch (err) {
      toast.error(err.message || "This receipt could not be opened");
    } finally {
      setOpening(null);
    }
  };

  if (p.loading && !p.data) return <PanelSkeleton tiles={3} />;
  if (p.error) return <PanelError error={p.error} onRetry={p.reload} />;

  const d = p.data || {};

  const columns = [
    {
      title: "Date", dataIndex: "transaction_date", width: 165,
      render: (v) => <span style={{ fontSize: 12.5, color: "#475569", whiteSpace: "nowrap" }}>{dateTime(v)}</span>,
    },
    {
      title: "Credit", dataIndex: "credit", width: 165, align: "right",
      /* The tax sits under the amount rather than in its own column: only
         recharge rows carry it, and a column that is empty on half the ledger
         costs width the rest of the row needs. Debit rows send null here, so
         nothing renders. */
      render: (v, r) => {
        if (!(Number(v) > 0)) return <span style={{ color: "#cbd5e1" }}>—</span>;
        const gst = r.gst_amount;
        const hasGst = gst !== null && gst !== undefined && gst !== "" && Number(gst) > 0;
        const base = baseOf(r);
        return (
          <div>
            <div style={{ color: "#16a34a", fontWeight: 700, fontVariantNumeric: "tabular-nums" }}>
              +{inr(v)}
            </div>
            {hasGst && (
              <div style={{ fontSize: 11, color: "#b45309", marginTop: 2, fontVariantNumeric: "tabular-nums" }}>
                {base !== null && <>{inr(base)} · </>}
                GST {inr(gst)}
                {r.gst_percent ? ` (${r.gst_percent}%)` : ""}
              </div>
            )}
          </div>
        );
      },
    },
    {
      title: "Debit", dataIndex: "debit", width: 120, align: "right",
      render: (v) => (Number(v) > 0
        ? <span style={{ color: "#dc2626", fontWeight: 700, fontVariantNumeric: "tabular-nums" }}>−{inr(v)}</span>
        : <span style={{ color: "#cbd5e1" }}>—</span>),
    },
    {
      title: "Reason", key: "reason", width: 200,
      /* One column for both sides — a row is only ever one of them, and two
         half-empty columns say less than one full one. */
      render: (_, r) => {
        const why = r.credit_source || r.debit_for;
        return why
          ? <span style={{ fontSize: 12.5, color: "#334155", fontWeight: 500 }}>{why}</span>
          : <span style={{ color: "#cbd5e1" }}>—</span>;
      },
    },
    {
      title: "Comment", dataIndex: "comment", width: 240,
      render: (v) => (v
        ? <Tooltip title={v}><span style={{ fontSize: 12.5, color: "#64748b" }}>{v}</span></Tooltip>
        : <span style={{ color: "#cbd5e1" }}>—</span>),
    },
    {
      title: "Balance", dataIndex: "total_amount", width: 130, align: "right",
      render: (v) => <span style={{ fontWeight: 600, color: "#0f172a", fontVariantNumeric: "tabular-nums" }}>{inr(v)}</span>,
    },
    { title: "Status", dataIndex: "status", width: 110, render: (v) => statusChip(v) },
    {
      title: "Receipt", key: "receipt", width: 120, fixed: "right",
      render: (_, r) => {
        if (!isRecharge(r)) return <span style={{ color: "#cbd5e1" }}>—</span>;
        return (
          <div style={{ display: "inline-flex", gap: 6 }}>
            <Tooltip title="Open the receipt">
              <button
                onClick={() => download(r)}
                disabled={opening === r.id}
                style={{
                  width: 30, height: 30, borderRadius: 8, border: "1px solid #ddd6fe",
                  background: "#f5f3ff", color: "#6366f1", cursor: "pointer",
                  display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 12,
                }}
              >
                <DownloadOutlined spin={opening === r.id} />
              </button>
            </Tooltip>
            <Tooltip title="Email this receipt">
              <button
                onClick={() => setSending({
                  invoice_number: r.invoice_number || `Wallet credit #${r.id}`,
                  title: r.credit_source || "Wallet recharge",
                  amount: r.credit ?? r.added_balance,
                  send_endpoint: `/api/invoices/wallet/${r.id}/send`,
                })}
                style={{
                  width: 30, height: 30, borderRadius: 8, border: "1px solid #bfdbfe",
                  background: "#eff6ff", color: "#2563eb", cursor: "pointer",
                  display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 12,
                }}
              >
                <MailOutlined />
              </button>
            </Tooltip>
          </div>
        );
      },
    },
  ];

  return (
    <>
      <TileGrid>
        <StatTile
          icon={<WalletOutlined />} label="Current balance" value={inr(d.current_balance)}
          color="#6366f1" bg="#eef2ff"
        />
        <StatTile
          icon={<ArrowUpOutlined />} label="Total added" value={inr(d.total_added)}
          color="#16a34a" bg="#f0fdf4"
        />
        <StatTile
          icon={<ArrowDownOutlined />} label="Total used" value={inr(d.total_used)}
          color="#dc2626" bg="#fef2f2"
        />
      </TileGrid>

      <div style={cardStyle}>
        <PanelHeader
          icon={<WalletOutlined />}
          title="Wallet ledger"
          subtitle="Every credit and debit against this account"
        />
        <PanelTable
          columns={columns}
          rows={p.rows}
          loading={p.loading}
          page={p.page}
          total={p.total}
          perPage={p.perPage}
          onPage={p.goToPage}
          empty="No wallet activity on this account yet."
          scrollX={1245}
        />
      </div>

      <SendInvoiceModal
        open={Boolean(sending)}
        invoice={sending}
        userId={userId}
        defaultEmail={customerEmail}
        onClose={() => setSending(null)}
        onSent={p.reload}
      />
    </>
  );
};

export default WalletTab;
